/**
 * NotificationDropdown - Notifications center dropdown
 *
 * Displays user notifications with actions.
 * Groups new items (emails, messages) by type with summary cards.
 * System notifications are displayed individually.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Bell,
  CheckCheck,
  Trash2,
  Info,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Mail,
  MessageCircle,
  Phone,
  FileText,
  Calendar,
  CreditCard,
  Users,
  ChevronRight,
} from 'lucide-react';
import { NotificationService, type Notification } from '../../services/NotificationService';
import { useNotificationsStore, type InAppNotification } from '@business-assistant/shared';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';

interface NotificationDropdownProps {
  onClose: () => void;
}

const NOTIFICATION_ICONS: Record<string, any> = {
  info: Info,
  warning: AlertTriangle,
  success: CheckCircle,
  error: XCircle,
};

const NOTIFICATION_COLORS: Record<string, { bg: string; color: string }> = {
  info: { bg: '#dbeafe', color: '#3b82f6' },
  warning: { bg: '#fef3c7', color: '#f59e0b' },
  success: { bg: '#dcfce7', color: '#22c55e' },
  error: { bg: '#fee2e2', color: '#ef4444' },
};

// Groupable notification types (items that should be shown as counts)
const GROUPABLE_TYPES = [
  'new_email',
  'new_message',
  'missed_call',
  'bank_transaction_received',
  'invoice_received',
  'invoice_sent',
  'task_assigned',
  'task_due_soon',
  'calendar_event',
];

// Icons for grouped notification types
const GROUP_ICONS: Record<string, any> = {
  new_email: Mail,
  new_message: MessageCircle,
  missed_call: Phone,
  bank_transaction_received: CreditCard,
  invoice_received: FileText,
  invoice_sent: FileText,
  task_assigned: CheckCircle,
  task_due_soon: AlertTriangle,
  calendar_event: Calendar,
  team_member_added: Users,
};

// Colors for grouped notification types
const GROUP_COLORS: Record<string, { bg: string; color: string }> = {
  new_email: { bg: '#dbeafe', color: '#3b82f6' },
  new_message: { bg: '#dcfce7', color: '#22c55e' },
  missed_call: { bg: '#fef3c7', color: '#f59e0b' },
  bank_transaction_received: { bg: '#e0e7ff', color: '#6366f1' },
  invoice_received: { bg: '#fce7f3', color: '#ec4899' },
  invoice_sent: { bg: '#d1fae5', color: '#10b981' },
  task_assigned: { bg: '#fef3c7', color: '#f59e0b' },
  task_due_soon: { bg: '#fee2e2', color: '#ef4444' },
  calendar_event: { bg: '#ede9fe', color: '#7c3aed' },
  team_member_added: { bg: '#dcfce7', color: '#22c55e' },
};

interface GroupedNotification {
  type: string;
  count: number;
  unreadCount: number;
  items: InAppNotification[];
  latestAt: string;
  navigateTo?: string;
}

export function NotificationDropdown({ onClose }: NotificationDropdownProps) {
  const { t, language } = useTranslation();
  const [serviceNotifications, setServiceNotifications] = useState<Notification[]>([]);
  const navigate = useNavigate();

  // Live notifications from workflow engine (emails, etc.)
  const liveNotifications = useNotificationsStore((s) => s.liveNotifications);
  const markLiveAsRead = useNotificationsStore((s) => s.markAsRead);
  const markAllLiveAsRead = useNotificationsStore((s) => s.markAllAsRead);
  const clearLiveNotifications = useNotificationsStore((s) => s.clearNotifications);

  // Load service notifications and subscribe to changes
  useEffect(() => {
    setServiceNotifications(NotificationService.getAll());

    const unsubscribe = NotificationService.subscribe((updated) => {
      setServiceNotifications(updated);
    });

    return unsubscribe;
  }, []);

  // Group live notifications by type (for groupable types)
  const { groupedNotifications, ungroupedLive } = useMemo(() => {
    const groups: Record<string, GroupedNotification> = {};
    const ungrouped: InAppNotification[] = [];

    for (const n of liveNotifications) {
      if (GROUPABLE_TYPES.includes(n.type)) {
        if (!groups[n.type]) {
          groups[n.type] = {
            type: n.type,
            count: 0,
            unreadCount: 0,
            items: [],
            latestAt: n.createdAt,
            navigateTo: getNavigateUrlForType(n.type),
          };
        }
        groups[n.type].count++;
        if (!n.read) groups[n.type].unreadCount++;
        groups[n.type].items.push(n);
        if (new Date(n.createdAt) > new Date(groups[n.type].latestAt)) {
          groups[n.type].latestAt = n.createdAt;
        }
      } else {
        ungrouped.push(n);
      }
    }

    // Sort groups by latest notification time
    const sortedGroups = Object.values(groups).sort(
      (a, b) => new Date(b.latestAt).getTime() - new Date(a.latestAt).getTime()
    );

    return { groupedNotifications: sortedGroups, ungroupedLive: ungrouped };
  }, [liveNotifications]);

  // Total unread count (grouped + ungrouped + service)
  const unreadCount = useMemo(() => {
    const groupedUnread = groupedNotifications.reduce((sum, g) => sum + g.unreadCount, 0);
    const ungroupedUnread = ungroupedLive.filter(n => !n.read).length;
    const serviceUnread = serviceNotifications.filter(n => !n.read).length;
    return groupedUnread + ungroupedUnread + serviceUnread;
  }, [groupedNotifications, ungroupedLive, serviceNotifications]);

  // Check if there are any items to display
  const hasItems = groupedNotifications.length > 0 || ungroupedLive.length > 0 || serviceNotifications.length > 0;

  const handleGroupClick = (group: GroupedNotification) => {
    // Mark all items in group as read
    group.items.forEach(item => markLiveAsRead(item.id));

    // Navigate to the appropriate view
    if (group.navigateTo) {
      navigate(group.navigateTo);
      onClose();
    }
  };

  const handleNotificationClick = (notification: Notification & { source?: 'live' }) => {
    // Mark as read in the appropriate store
    if (notification.source === 'live') {
      markLiveAsRead(notification.id);
    } else {
      NotificationService.markAsRead(notification.id);
    }

    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
    }
  };

  const handleLiveNotificationClick = (notification: InAppNotification) => {
    markLiveAsRead(notification.id);
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
    }
  };

  const handleMarkAllRead = () => {
    NotificationService.markAllAsRead();
    markAllLiveAsRead();
  };

  const handleClearAll = () => {
    if (confirm(t('notificationsPanel.clearAllConfirm'))) {
      NotificationService.clearAll();
      clearLiveNotifications();
    }
  };

  const handleDelete = (id: string, source: 'live' | undefined, e: React.MouseEvent) => {
    e.stopPropagation();
    if (source === 'live') {
      // For live notifications, just mark as read (no delete in store yet)
      markLiveAsRead(id);
    } else {
      NotificationService.delete(id);
    }
  };

  return (
    <div style={{
      position: 'absolute',
      top: '60px',
      right: '16px',
      width: '380px',
      maxWidth: 'calc(100vw - 32px)',
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      border: '1px solid #e5e7eb',
      zIndex: 1000,
      maxHeight: '500px',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
            {t('notificationsPanel.title')}
            {unreadCount > 0 && (
              <span style={{
                marginLeft: '8px',
                padding: '2px 8px',
                background: '#3b82f6',
                color: 'white',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 500,
              }}>
                {unreadCount}
              </span>
            )}
          </h3>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              style={{
                background: 'none',
                border: 'none',
                color: '#3b82f6',
                fontSize: '12px',
                cursor: 'pointer',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
              title={t('notificationsPanel.markAllRead')}
            >
              <CheckCheck size={14} />
              {t('notificationsPanel.markAllRead')}
            </button>
          )}
          {notifications.length > 0 && (
            <button
              onClick={handleClearAll}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                fontSize: '12px',
                cursor: 'pointer',
                padding: '4px',
              }}
              title={t('notificationsPanel.clearAll')}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Notifications list */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
      }}>
        {!hasItems ? (
          // Empty state
          <div style={{
            padding: '48px 24px',
            textAlign: 'center',
            color: '#6b7280',
          }}>
            <Bell size={32} style={{ opacity: 0.3, marginBottom: '8px' }} />
            <div style={{ fontSize: '14px' }}>{t('notificationsPanel.noNotifications')}</div>
            <div style={{ fontSize: '12px', marginTop: '4px' }}>{t('notificationsPanel.notificationsAppearHere')}</div>
          </div>
        ) : (
          <div style={{ padding: '8px' }}>
            {/* Grouped notifications (new items by type) */}
            {groupedNotifications.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#9ca3af',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  padding: '4px 8px',
                  marginBottom: '4px',
                }}>
                  {t('notificationsPanel.newItems')}
                </div>
                {groupedNotifications.map((group) => {
                  const Icon = GROUP_ICONS[group.type] || Info;
                  const colors = GROUP_COLORS[group.type] || NOTIFICATION_COLORS.info;
                  const hasUnread = group.unreadCount > 0;

                  return (
                    <div
                      key={group.type}
                      onClick={() => handleGroupClick(group)}
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: '4px',
                        background: hasUnread ? 'white' : '#f9fafb',
                        opacity: hasUnread ? 1 : 0.6,
                        border: '1px solid',
                        borderColor: hasUnread ? colors.bg : '#e5e7eb',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateX(-4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'none';
                      }}
                    >
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        {/* Icon */}
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: colors.bg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <Icon size={20} style={{ color: colors.color }} />
                        </div>

                        {/* Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: 600,
                            fontSize: '14px',
                            color: hasUnread ? '#111827' : '#6b7280',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}>
                            {getGroupLabel(group.type, group.count, t)}
                            {hasUnread && (
                              <span style={{
                                padding: '2px 6px',
                                background: colors.color,
                                color: 'white',
                                borderRadius: '10px',
                                fontSize: '11px',
                                fontWeight: 600,
                              }}>
                                {group.unreadCount}
                              </span>
                            )}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#9ca3af',
                            marginTop: '2px',
                          }}>
                            {formatRelativeTime(group.latestAt, t, language)}
                          </div>
                        </div>

                        {/* Arrow */}
                        <ChevronRight size={16} style={{ color: '#9ca3af' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Ungrouped live notifications (non-groupable types) */}
            {ungroupedLive.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                {ungroupedLive.map((notification) => {
                  const Icon = GROUP_ICONS[notification.type] || Info;
                  const colors = GROUP_COLORS[notification.type] || NOTIFICATION_COLORS.info;

                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleLiveNotificationClick(notification)}
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        cursor: notification.actionUrl ? 'pointer' : 'default',
                        marginBottom: '4px',
                        background: notification.read ? '#f9fafb' : 'white',
                        opacity: notification.read ? 0.6 : 1,
                        border: '1px solid',
                        borderColor: notification.read ? '#e5e7eb' : colors.bg,
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        if (notification.actionUrl) {
                          e.currentTarget.style.transform = 'translateX(-4px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'none';
                      }}
                    >
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: colors.bg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <Icon size={16} style={{ color: colors.color }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: 600,
                            fontSize: '13px',
                            marginBottom: '4px',
                            color: notification.read ? '#6b7280' : '#111827',
                          }}>
                            {notification.title}
                          </div>
                          <div style={{
                            fontSize: '13px',
                            color: '#6b7280',
                          }}>
                            {notification.message}
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                            {formatRelativeTime(notification.createdAt, t, language)}
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleDelete(notification.id, 'live', e)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#6b7280',
                            cursor: 'pointer',
                            padding: '4px',
                            opacity: 0.6,
                          }}
                          title={t('notificationsPanel.delete')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* System notifications (from NotificationService) */}
            {serviceNotifications.length > 0 && (
              <div>
                {(groupedNotifications.length > 0 || ungroupedLive.length > 0) && (
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#9ca3af',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 8px',
                    marginBottom: '4px',
                  }}>
                    {t('notificationsPanel.systemNotifications')}
                  </div>
                )}
                {serviceNotifications.map((notification) => {
                  const Icon = NOTIFICATION_ICONS[notification.type] || Info;
                  const colors = NOTIFICATION_COLORS[notification.type] || NOTIFICATION_COLORS.info;

                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        cursor: notification.actionUrl ? 'pointer' : 'default',
                        marginBottom: '4px',
                        background: notification.read ? '#f9fafb' : 'white',
                        opacity: notification.read ? 0.6 : 1,
                        border: '1px solid',
                        borderColor: notification.read ? '#e5e7eb' : colors.bg,
                        position: 'relative',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        if (notification.actionUrl) {
                          e.currentTarget.style.transform = 'translateX(-4px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'none';
                      }}
                    >
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: colors.bg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <Icon size={16} style={{ color: colors.color }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: 600,
                            fontSize: '13px',
                            marginBottom: '4px',
                            color: notification.read ? '#6b7280' : '#111827',
                          }}>
                            {notification.title}
                          </div>
                          <div style={{
                            fontSize: '13px',
                            color: '#6b7280',
                            marginBottom: '8px',
                          }}>
                            {notification.message}
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '8px',
                          }}>
                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                              {formatRelativeTime(notification.createdAt, t, language)}
                            </span>
                            {notification.actionLabel && notification.actionUrl && (
                              <span style={{
                                fontSize: '12px',
                                color: '#3b82f6',
                                fontWeight: 500,
                              }}>
                                {notification.actionLabel} →
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleDelete(notification.id, undefined, e)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#6b7280',
                            cursor: 'pointer',
                            padding: '4px',
                            opacity: 0.6,
                          }}
                          title={t('notificationsPanel.delete')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Get navigation URL for a grouped notification type
 */
function getNavigateUrlForType(type: string): string | undefined {
  switch (type) {
    case 'new_email':
      return '/hub?channel=email';
    case 'new_message':
      return '/hub?channel=whatsapp';
    case 'missed_call':
      return '/hub?channel=calls';
    case 'bank_transaction_received':
      return '/accounting?tab=bank';
    case 'invoice_received':
    case 'invoice_sent':
      return '/accounting?tab=invoices';
    case 'task_assigned':
    case 'task_due_soon':
      return '/today';
    case 'calendar_event':
      return '/calendar';
    default:
      return undefined;
  }
}

/**
 * Get localized label for a grouped notification type
 */
function getGroupLabel(
  type: string,
  count: number,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  // Use singular or plural key based on count
  const suffix = count === 1 ? '' : '_plural';
  const key = `notificationsPanel.groups.${type}${suffix}`;

  const fallbacks: Record<string, string> = {
    new_email: count === 1 ? '1 new email' : `${count} new emails`,
    new_message: count === 1 ? '1 new message' : `${count} new messages`,
    missed_call: count === 1 ? '1 missed call' : `${count} missed calls`,
    bank_transaction_received: count === 1 ? '1 new transaction' : `${count} new transactions`,
    invoice_received: count === 1 ? '1 invoice received' : `${count} invoices received`,
    invoice_sent: count === 1 ? '1 invoice sent' : `${count} invoices sent`,
    task_assigned: count === 1 ? '1 task assigned' : `${count} tasks assigned`,
    task_due_soon: count === 1 ? '1 task due soon' : `${count} tasks due soon`,
    calendar_event: count === 1 ? '1 upcoming event' : `${count} upcoming events`,
  };

  const translated = t(key, { count });
  // If translation key not found (returns the key itself), use fallback
  return translated === key ? (fallbacks[type] || `${count} ${type}`) : translated;
}

/**
 * Format relative time (e.g., "2 min ago", "1 hour ago")
 */
function formatRelativeTime(
  isoDate: string,
  t: (key: string, options?: Record<string, unknown>) => string,
  language: string
): string {
  const now = new Date();
  const then = new Date(isoDate);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('notificationsPanel.relativeTime.now');
  if (diffMins < 60) return t('notificationsPanel.relativeTime.minutesAgo', { count: diffMins });
  if (diffHours < 24) return t('notificationsPanel.relativeTime.hoursAgo', { count: diffHours });
  if (diffDays === 1) return t('notificationsPanel.relativeTime.yesterday');
  if (diffDays < 7) return t('notificationsPanel.relativeTime.daysAgo', { count: diffDays });

  const locale = language === 'it' ? 'it-IT' : 'en-US';
  return then.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
}
